import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { stockUnitsApi } from '../stockApi';
import type { StockUnitListItem, StockUnitStatus } from '../stockTypes';
import { UNIT_STATUS } from '../stockTypes';

/**
 * Take one serialized unit off the owned tally.
 *
 * **The two endings write DIFFERENT ledger types, and the difference is the whole point.**
 * Disposed is a disposal — this organization destroyed the thing. Stolen is an adjustment: the
 * unit left the tally without anybody disposing of anything, and filing a theft under Disposal
 * would make a disposals report state that we destroyed property somebody took.
 *
 * **Only one of them is reversible**, and the form says so before the tick rather than leaving
 * the reader to find out. A stolen unit that turns up comes back; a disposal is a certified
 * destruction and is corrected by a new record rather than an undo.
 */
export function WriteOffStockUnitModal({
  open,
  unit,
  onClose,
  onSaved,
}: {
  open: boolean;
  unit: StockUnitListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  /*
    Seeded EMPTY, never to one side. This is a substantive claim about what happened to the
    thing — recording "disposed" for a reader who said nothing would assert a destruction
    nobody certified — so the question stays open until it is answered, and the primary action
    stays disabled meanwhile.
  */
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const disposing = status === String(UNIT_STATUS.disposed);

  const valid = status !== '' && reason.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await stockUnitsApi.writeOff(unit.id, {
        status: Number(status) as StockUnitStatus,
        reason: reason.trim(),
      });

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not write off this unit.', 0);

      setError(failure);

      // The status radios put their id on each OPTION, so the derived id resolves to nothing —
      // map it to the first, which is where a keyboard reader enters a radio group anyway.
      focusValidationError(failure, {
        idPrefix: 'write-off-unit',
        fieldIds: { status: `write-off-unit-status-${UNIT_STATUS.disposed}` },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Write off unit"
      size="md"
      id="write-off-unit-modal"
      /*
        NO Autogenerate. A generated write-off appends a ledger line asserting a unit left the
        owned tally, and one of the two endings cannot be undone — a button that fabricates
        that is a button that eventually fabricates it on a real asset.
      */
      confirm={{
        id: 'write-off-unit-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="write-off-unit-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="write-off-unit-submit"
            variant="danger"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Recording"
          >
            Write off
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Serial', value: unit.serialNumber, mono: true },
            { label: 'Item', value: `${unit.sku} — ${unit.itemName}` },
            { label: 'Facility', value: unit.storageFacilityName },
          ]}
        />

        <RadioGroup
          id="write-off-unit-status"
          label="What happened to it?"
          required
          value={status}
          onChange={setStatus}
          disabled={saving}
          options={[
            {
              value: String(UNIT_STATUS.disposed),
              label: 'Disposed',
              description:
                'Condemned and destroyed. Recorded as a disposal, and not undone by putting the row back — if it turns up, the disposal was wrong and the correction is a new record.',
            },
            {
              value: String(UNIT_STATUS.stolen),
              label: 'Stolen or lost',
              description:
                'Gone, but nobody disposed of anything — recorded as an adjustment rather than a disposal. Reversible: if it turns up, Return to stock puts it back.',
            },
          ]}
          error={error?.fieldError('status')}
        />

        {/* The consequence goes on the FORM, before the tick — never appended to the thing the
            reader is already clicking past. */}
        {disposing ? (
          <p className="text-sm text-slate-600">
            This is the ending that cannot be reversed here. It appends a disposal to the ledger
            asserting the unit was destroyed.
          </p>
        ) : null}

        <TextField
          id="write-off-unit-reason"
          label="Why?"
          required
          description="Recorded on the ledger line and on the unit. Beyond economical repair, reported missing after a count, condemned on inspection."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('reason')}
        />
      </div>
    </Modal>
  );
}
